import { CONFIG } from '../../Config/config'
import store from '../../Config/store'
import * as ERRORS from '../../Reducers/Errors/const'
import {
  iteratePromisesInChunks,
  splitArrayIntoChunks,
} from '../../shared/libraries/utils/array'
import { apiClient, fetchRetry } from '../API/Client'
import { authorization } from '../Auth/Auth'
import {
  getInventory as getInventoryGraphQLFunc,
  getInventoryItem as getInventoryItemGraphQLFunc,
  processInventoryMovements as processInventoryMovementsGraphQLFunc,
} from './../../shared/graphql/functions/inventory'

/**
 * Parse a GraphQL response body, tolerating base64-encoded payloads.
 *
 * Promofy's API gateway returns some responses (notably GraphQL error
 * responses, e.g. HTTP 400) base64-encoded but WITHOUT a Content-Encoding
 * header, so response.json() throws "Unexpected token ... is not valid JSON".
 * That rejection was unhandled by callers (e.g. the BA pipeline calendar
 * fetch) and left the page stuck on an infinite loading spinner. Decode and
 * retry before giving up.
 *
 * @param {Response} response
 * @returns {Promise<object>}
 */
const parseGraphQLBody = async response => {
  const text = await response.text()
  const trimmed = text.trim()
  if (!trimmed) {
    return {}
  }
  try {
    return JSON.parse(trimmed)
  } catch (parseError) {
    try {
      return JSON.parse(atob(trimmed))
    } catch (decodeError) {
      throw parseError
    }
  }
}

/**
 * Call Graph QL Endpoint
 * @param {string} query
 * @param {object} variables
 * @returns {Promise<object>}
 */
export const callGraphQLEndpoint = function(
  authToken,
  query,
  variables,
  options = {},
  fetchOptions = {},
  withCookies = true,
  retryOptions = {
    delay: 1000,
    tries: 3,
  },
) {
  const body = {
    query,
    variables,
  }

  return new Promise((resolve, reject) => {
    const getCache = options
      ? options.getCache
        ? options.getCache
        : false
      : false

    const headers = {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': 'Bearer ' + authToken } : {}),
      'x-oppizi-cache': getCache,
    }

    const request = apiClient.prepareRequest(
      CONFIG.GRAPHQL_API_BASE_URL,
      'POST',
      body,
      headers,
      fetchOptions,
      withCookies,
    )

    fetchRetry(request, retryOptions.delay, retryOptions.tries)
      .then(async response => {
        const result = await response.clone()
        const jsonResult = await parseGraphQLBody(result)
        if (jsonResult.errors && !options.ignoreErrors) {
          const error = jsonResult.errors[0]
          if (!options.doNotShowErrorModal) {
            store.dispatch({
              type: ERRORS.PUSH_API_ERROR,
              error,
            })
          }
          reject(error)
        } else {
          resolve(response)
        }
      })
      .catch(error => {
        // Previously this called resolve(error), which handed callers an
        // Error in place of a Response. Awaiters then did response.json()
        // and crashed with "n.json is not a function" — surfaced to users
        // as e.g. "Overlay saving failed". Reject so try/catch can handle.
        // Preserve legacy hang-on-ignoreErrors behavior for bulk callers
        // that depend on it.
        if (!options.ignoreErrors) {
          reject(error)
        }
      })
  })
}

export const callAuthorizedGraphQLEndpoint = (
  query,
  variables,
  options,
  fetchOptions,
) => {
  const authToken = authorization.getAPIKey()
  return callGraphQLEndpoint(authToken, query, variables, options, fetchOptions)
}

export const callTrailblazer = (query, variables) => {
  const authToken = authorization.getAPIKey()
  var body = JSON.stringify({
    query,
    variables,
  })
  return fetch(CONFIG.TRAILBLAZER_API_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': 'Bearer ' + authToken } : {}),
    },
    body,
  })
}

/**
 * Process an array of inventory movements
 * Example movement
 * {
 *   "quantity": 51,
 *   "inventoryId": 1,
 *   "cityId": 1,
 *   "comments": "Test",
 *   "ambassadorId": 1,
 *   "movement": 1,
 *   "dateTime": "2019-11-05T11:27:00+10:00",
 *   "relatedCampaign": 1236
 * }
 *
 * @param {Array<Movement>} movements
 * @returns {Promise<Array<null|Error>>}
 */
function processInventoryMovements(authToken, movements) {
  return callGraphQLEndpoint(authToken, processInventoryMovementsGraphQLFunc, {
    movements,
  })
}

/**
 * Get inventory
 * @param {string} authToken
 * @param {object} params
 */
function getInventory(
  authToken,
  first,
  after,
  {
    cityId,
    searchText,
    clientId,
    typeId,
    ambassadorId,
    current,
    campaignId,
    active,
    includeLeftovers,
  },
) {
  return callGraphQLEndpoint(authToken, getInventoryGraphQLFunc, {
    first,
    after,
    cityId,
    clientId,
    typeId,
    searchText,
    current,
    ambassadorId,
    campaignId,
    active,
    includeLeftovers,
  })
}

/**
 * Get or search for inventory items.
 * @param {number} first Get the first x records, limit is 500
 * @param {string} after The cursort to start getting the records from
 * @param {number} cityId To filter inventory by this city
 * @param {number} clientId To filter inventory by this client
 * @param {number} typeId To filter inventory by this type
 * @param {string} searchText To search by inventory description
 * @param {number} ambassadorId To filter by ambassador
 * @param {Boolean} current Can only specify if ambassadorId is also specified.
 * If set to true then it will only return the inventory that the ambassador
 * currently has. If false it returns inventory the ambassador has had in the
 * past.
 * @returns {Array<InventoryItem>}
 */
const getPickupInventoryGraphQLFunc = `
  query getPickupInventoryData(
    $first: Int
    $merchCursor: String
    $flyerCodesCursor: String
    $cityId: ID!
    $clientIds: [ID]
    $searchText: String
    $searchFromStart: Boolean
    $ambassadorIds: [ID]
    $current: Boolean
    $campaignIds: [ID]
    $active: Boolean
    $includeLeftovers: Boolean
  ) {

    inventoryTypes {
      edges {
        node {
          id
          name
        }
      }
    }

    merch: inventoryItems(
      first: $first
      after: $merchCursor
      cityId: $cityId
      clientIds: $clientIds
      excludedTypeIds: [3] # Exclude flyers
      searchText: $searchText
      searchFromStart: $searchFromStart
      ambassadorIds: $ambassadorIds
      current: $current
      # When searching for merch we don't want to exclude by campaign ids
      # campaignIds: $campaignIds
      active: $active
      includeLeftovers: $includeLeftovers
    ){
      pageInfo {
        endCursor,
        hasNextPage
      },
      totalCount,
      edges {
        node {
          id,
          client { id },
          inventoryType { id },
          description,
          summarisedMovements {
            nodes {
              quantity,
              movementType { id },
              relatedUser {
                id,
                firstName,
                lastName,
              },
              relatedCity { id }
            }
          }
        }
      }
    }

    flyerCodes: inventoryItems(
      first: $first
      after: $flyerCodesCursor
      cityId: $cityId
      clientIds: $clientIds
      typeIds: [3] # Include only flyer codes
      searchText: $searchText
      searchFromStart: $searchFromStart
      ambassadorIds: $ambassadorIds
      current: $current
      # Filter by campaign for flyer codes
      campaignIds: $campaignIds
      active: $active
      includeLeftovers: $includeLeftovers
    ){
      pageInfo {
        endCursor,
        hasNextPage
      },
      totalCount,
      edges {
        node {
          id,
          client { id },
          inventoryType { id },
          description,
          tag
          campaignId
          summarisedMovements {
            nodes {
              quantity,
              movementType { id },
              relatedUser {
                id,
                firstName,
                lastName,
              },
              relatedCity { id }
            }
          }
        }
      }
    }
  }
`

/**
 * Get pickup data
 * @param {string} authToken
 * @param {object} params
 */
function getPickupInventoryData(
  authToken,
  first,
  { merchCursor, flyerCodesCursor },
  {
    cityId,
    searchText,
    clientIds,
    typeId,
    ambassadorIds,
    current,
    campaignIds,
    active,
    includeLeftovers,
  },
) {
  return callGraphQLEndpoint(authToken, getPickupInventoryGraphQLFunc, {
    first,
    merchCursor,
    flyerCodesCursor,
    cityId,
    clientIds,
    typeId,
    searchText,
    current,
    ambassadorIds,
    campaignIds,
    active,
    includeLeftovers,
  })
}

/**
 * Add an inventory item
 * @param {InventoryItemInput} item Required props are clientId, invento
 */
const addInventoryItem = function(authToken, item) {
  return callGraphQLEndpoint(
    authToken,
    `mutation addInventoryItem(
      $input: AddInventoryItemInput
    ) {
      addInventoryItem(
        input: $input,
      ){
        id,
        client { id },
        inventoryType { id },
        description,
        tag,
        movements {
          nodes {
            id,
            quantity,
            movementType { id },
            relatedUser { id },
            relatedCity { id },
            comments,
            dateTime

          }
        }
      }
    }`,
    {
      input: item,
    },
  )
}

/**
 * Add flyer codes in bulk
 * @param {Array<Object>} items
 */
const addFlyerCodes = async (authToken, flyerCodes, onProgress) => {
  const mutation = `mutation addFlyerCodes(
    $input: [AddFlyerCodeInput]
  ) {
    addFlyerCodes(
      input: $input,
    ){
      flyerCodes {
        id,
        client { id },
        inventoryType { id },
        description,
        tag,
        movements {
          nodes {
            id,
            quantity,
            movementType { id },
            relatedUser { id },
            relatedCity { id },
            comments,
            dateTime
          }
        }
      }
    }
  }`

  const itemsPerChunk = 50

  const chunks = splitArrayIntoChunks({
    inputArray: flyerCodes,
    itemsPerChunk,
  })

  let createdFlyerCodes = []

  if (onProgress && !createdFlyerCodes.length) {
    onProgress({
      itemsPerChunk,
      processedAmount: 0,
      totalAmount: 100,
    })
  }

  for (let index = 0; index < chunks.length; index++) {
    const codesInChunk = chunks[index]
    const variables = {
      input: codesInChunk,
    }

    const request = await callAuthorizedGraphQLEndpoint(mutation, variables)
    const result = await request.json()
    createdFlyerCodes = [
      ...createdFlyerCodes,
      ...((result.data.addFlyerCodes && result.data.addFlyerCodes.flyerCodes) ||
        []),
    ]
    if (onProgress) {
      onProgress({
        itemsPerChunk,
        processedAmount: createdFlyerCodes.length,
        totalAmount: flyerCodes.length,
      })
    }
  }

  return createdFlyerCodes
}

const deleteFlyerCodeById = async (authToken, id) =>
  callGraphQLEndpoint(
    authToken,
    `mutation deleteFlyerCode(
      $input: DeleteFlyerCodeInput
    ) {
      deleteFlyerCode(
        input: $input,
      )
    }`,
    {
      input: { id },
    },
  )

/**
 * Get inventory item
 * @param {string} authToken
 * @param {number} id
 */
function getInventoryItem(authToken, id) {
  return callGraphQLEndpoint(authToken, getInventoryItemGraphQLFunc, {
    id,
  })
}

/**
 * Gets the mission results for a campaign.
 * @param {string} authToken
 * @param {number} campaignId
 */
function getCampaignResults(authToken, campaignId) {
  var query = `query ($campaignId:ID!) {
    campaign(id:$campaignId){
      id
      name
      pitchUrl
      type
      bonusLimit
      status
      metrics {
        nodes {
          id
          name
          targetConversionRate
          targetCostPerMetric
          isPrimary
        }
      }
      client {
        id
        logo
      }
      cityPlans {
        nodes {
          id
          payout
          distributionCost
          missions {
            edges {
              node {
                id
                flyerCode
                status
                totalFlyersDistributed
                dateTime
                distributionCost
                draftPayAdjustment
                printingCostPerFlyer
                packSize
                bonusPerConversion
                bonusRedemptionPeriod
                cancelled
                automatic
                type
                label
                user {
                  id
                  firstName
                  lastName
                }
                location {
                  id
                  name
                  hasPermit
                  rank
                  detailedRank
                  type {
                    name
                  }
                }
                metricResults {
                  nodes {
                    id
                    value
                    metric {
                      id
                    }
                  }
                }
                conversions {
                  nodes {
                    id
                    timestamp
                    metric {
                      id
                    }
                  }
                }
                boundary {
                  latitude
                  longitude
                }
                boundaryColour
              }
            }
          }
          city {
            id
            name
            lunchCheckInTime
            afternoonCheckInTime
            teamLeaderEnabled
            latitude
            longitude
            country {
              id
              name
              code
            }
            locations{
              edges {
                node {
                  id
                  name
                  hasPermit
                  rank
                  detailedRank
                  type {
                    name
                  }
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
          }
          permitCosts {
            nodes {
              id
              cost
              startDate
            }
          }
        }
      }
    }
  }`

  return callGraphQLEndpoint(authToken, query, {
    campaignId,
  })
}

/**
 * @typedef GetMissionsByCityQueryVars
 * @property {number} cityId Mandatory city id
 * @property {number} clientId Filter by client
 * @property {number} locationId Filter by location
 * @property {string} statusAndAbove Filter by status
 * @property {string} statusAndBelow Filter by status
 * @property {string} timeOfDay Filter by time of day
 * @property {number} campaignId Filter by campaign
 * @property {string} startDate Filter by start date
 * @property {string} endDate Filter by end date
 * @property {string} dayOfTheWeek Filter by day of the week
 *
 * Get missions within a city
 * @param {GetMissionsByCityQueryVars} queryVars
 */
const getMissionsByCity = function(authToken, queryVars) {
  var query = `query (
    $first:Int
    $skip:Int
    $cityId:ID!
    $clientId:ID
    $locationId:ID
    $campaignId:ID
    $startDate:String
    $endDate:String
    $statusAndAbove: MissionStatusEnum
    $statusAndBelow: MissionStatusEnum
    $timeOfDay: MissionTimeOfDayEnum
    $filter:String
    $orderBy:MissionOrderByEnum
    $dayOfTheWeek:DayOfTheWeekEnum
    $reported: Boolean
  ) {
    city(id: $cityId){
      id
      name
      country { id }
      lunchCheckInTime
      afternoonCheckInTime
      missions(
        first: $first
        skip: $skip
        clientId: $clientId
        locationId: $locationId
        campaignId: $campaignId
        startDate: $startDate
        endDate: $endDate
        statusAndAbove: $statusAndAbove
        statusAndBelow: $statusAndBelow
        timeOfDay: $timeOfDay
        filter: $filter
        orderBy: $orderBy
        dayOfTheWeek: $dayOfTheWeek
        reported: $reported
      ){
        edges {
          node {
            id
            dateTime
            flyerCode
            flyerCodeV2 {
              tag
              code
            }
            status
            totalFlyersDistributed
            cityPlan {
              campaign {
                client {
                  name
                }
                metrics {
                  nodes {
                    id
                    name
                  }
                }
              }
              city {
                afternoonCheckInTime
                lunchCheckInTime
              }
            }
            location {
              name
            }
            user {
              firstName
              lastName
            }
            metricResults {
              nodes {
                value
                metric {
                  id
                  name
                }
              }
            }
            amountScans
            canBeSpied
            spyInformation{
              spy{
                id
                firstName
                lastName
              }
              spyDetails{
                checkInTime
                checkoutTime
                reportReason
                }
            }
          }

        }
        pageInfo {
          hasNextPage
        }
        totalCount
      }
    }
  }`

  return callGraphQLEndpoint(authToken, query, queryVars)
}

const getMasterScheduleDetails = function(authToken, queryVars) {
  const query = `query ($campaignId:ID!) {
    campaign(id:$campaignId){
      cityPlans {
        nodes {
          city {
            brandAmbassadors {
              totalCount
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
            locations {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query, queryVars)
}

/**
 * @typedef UpdateUserData
 * @property {number} status User status. enum[active|blocked]
 * @property {number} blockedReason Reason of user block operation. Required for status == 'blocked'
 *
 * Update user information, such as status and block reason
 * @param {number} userId User ID
 * @param {UpdateUserData} userData
 */
const updateUserData = function(authToken, userId, userData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation updateUserData ($input: UpdateUserInput) {
      updateUserData(input: $input){
        user {
          id
          firstName
          lastName
          blockedReason
          status
          isTeamLeader
        }
      }
    }`,
    {
      input: { id: userId, ...userData },
    },
  )
}

/**
 * @typedef UpdateCity
 * @property {boolean} recruitmentOnHold Whether this city recruits new BAs or not
 *
 * Update city information, such as `recruitmentOnHold`
 * @param {number} cityId City ID
 * @param {UpdateCity} cityData
 */
const updateCity = function(authToken, cityId, cityData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation updateCitySettings ($input: UpdateCityInput) {
      updateCitySettings(input: $input){
        city {
          id
          recruitmentOnHold
          name
          timezone
          statusV2
          newcomersMissionsLimit
          cbdRadius
          pickupInformation
          morningCheckInTime
          lunchCheckInTime
          afternoonCheckInTime
          checkinRadius
          payHourly
          recruitmentCalendarId
          onboardingLocation
          onboardingUrl
          teamLeaderEnabled
          isBaDocumentManagementEnabled
          isOnsiteTrainingEnabled
          location {
            latitude
            longitude
          }
          h2hCheckInTimeBefore
          h2hCheckInTimeAfter
          h2hBonusCheckInTimeBefore
          h2hBonusCheckInTimeAfter
          d2dCheckInTimeBefore
          d2dCheckInTimeAfter
          d2dBonusCheckInTimeBefore
          d2dBonusCheckInTimeAfter
        }
      }
    }`,
    {
      input: { cityId, ...cityData },
    },
  )
}

/**
 * Create a city
 */
const createCity = function(authToken, cityData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation createCity ($input: CreateCityInput) {
      createCity(input: $input){
        city {
          id
          recruitmentOnHold
          name
          timezone
          statusV2
          newcomersMissionsLimit
          cbdRadius
          pickupInformation
          morningCheckInTime
          lunchCheckInTime
          afternoonCheckInTime
          checkinRadius
          payHourly
          recruitmentCalendarId
          onboardingLocation
          onboardingUrl
          location {
            latitude
            longitude
          }
          h2hCheckInTimeBefore
          h2hCheckInTimeAfter
          h2hBonusCheckInTimeBefore
          h2hBonusCheckInTimeAfter
          d2dCheckInTimeBefore
          d2dCheckInTimeAfter
          d2dBonusCheckInTimeBefore
          d2dBonusCheckInTimeAfter
        }
      }
    }`,
    {
      input: cityData,
    },
  )
}

/**
 * Create a city spy settings
 */
const creatCitySpySettings = function(spySettingsData) {
  return callAuthorizedGraphQLEndpoint(
    `mutation ($input: CreateSpyCitySettingsInput!) {
      createSpyCitySettings(input:$input){
          id
          cityId
          hourlyRate
          maxBudgetPerWeek
          travelAllowancePerMission
          travelAllowanceCap
          durationHandToHandMission
          durationLetterboxMission
          durationShift
          missionsPerShift
      }
    }`,
    {
      input: spySettingsData,
    },
  )
}

/**
 * Update a city spy settings
 */
const updateCitySpySettings = function(spySettingsData) {
  return callAuthorizedGraphQLEndpoint(
    `mutation ($input: UpdateSpyCitySettingsInput!) {
      updateSpyCitySettings(input:$input){
          id
          cityId
          hourlyRate
          maxBudgetPerWeek
          travelAllowancePerMission
          travelAllowanceCap
          durationHandToHandMission
          durationLetterboxMission
          durationShift
          missionsPerShift
      }
    }`,
    {
      input: spySettingsData,
    },
  )
}

/**
 * Update a city ba settings
 */
const updateBaCitySettingsGQL = function(baSettingsData) {
  return callAuthorizedGraphQLEndpoint(
    `mutation ($input: UpdateBaCitySettingsInput!) {
      updateBaCitySettings(input:$input){
        id
        cityId
        baWeeklyMissionsLimit
        tlaWeeklyMissionsLimit
      }
    }`,
    {
      input: baSettingsData,
    },
  )
}

export const chatParticipantsSearch = (
  authToken,
  searchedString,
  selectedCities,
  selectedRole,
) => {
  const query = `
  query ($searchedString: String!, $cityIds: [ID], $selectedRole: String) {
    chatParticipantsSearch(searchedString: $searchedString, cityIds: $cityIds, selectedRole: $selectedRole ){
      cities {
        id
        name
        type
      }
      users {
        id
        name
        type
        cityId
        role
        profilePicture
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query, {
    searchedString,
    cityIds: selectedCities,
    selectedRole,
  })
}
const deleteUser = function(authToken, userId) {
  return callGraphQLEndpoint(
    authToken,
    `mutation deleteUser ($input: DeleteUserInput) {
      deleteUser(input: $input){
        user {
          status
        }
      }
    }`,
    {
      input: { id: userId },
    },
  )
}

/**
 * Gets ambassador profile by id
 */
const getAmbassador = async (authToken, { id }) =>
  callGraphQLEndpoint(
    authToken,
    `query ($id:ID!) {
      brandAmbassador(ambassadorId:$id){
        id
        firstName
        lastName
        status
        blockedReason
        referralCode
        businessNumber
        insuranceNumber
        documentsProvided
        email
        fixedPriceMissions
        flyerCount
        isVerified
        itemCount
        memberSince
        mobilePhone
        profilePicture
        withholdPay
        isTeamLeader
        restrictedMissionType
        dateOfBirth
        contractorType
        paymentDetails {
          accountName
          accountNumber
          branchNumber
        }
        city {
          id
          name
          teamLeaderEnabled
          country {
            code
          }
        }
        formW9 {
          fileName
          fileSaveTime
        }
        missions{
          edges {
            node {
              id
              status
              dateTime
              cityPlan {
                city {
                  name
                }
                campaign {
                  client {
                    name
                  }
                }
              }
              location {
                name
              }
              type
              totalFlyersDistributed
            }
          }
          totalCount
        }
        signedLegalDocuments {
          totalCount
          edges {
            node {
              dateTime
              legalDocument {
                id
                url
                name
              }
            }
          }
        }
        germanyContractData {
          id
          address
          exactAddress
          houseNumber
          city
          postalCode
        }
        germanyPayrollData {
          id
          middleName
        }
        category
        germanyPaymentId
      }
    }`,
    { id },
  )

/**
 * Gets list of ambassador profile by city id
 */
const getAmbassadorsList = async (authToken, { cityId }) =>
  callGraphQLEndpoint(
    authToken,
    `query ($cityId:ID!) {
    brandAmbassadors(cityId:$cityId){
      totalCount
      edges{
        node {
          id
          firstName
          lastName
          status
          blockedReason
          referralCode
          businessNumber
          insuranceNumber
          documentsProvided
          email
          fixedPriceMissions
          flyerCount
          isVerified
          itemCount
          memberSince
          mobilePhone
          profilePicture
          withholdPay
          isTeamLeader
          paymentDetails {
            accountName
            accountNumber
            branchNumber
          }
          city {
            id
            name
          }
          missions{
            totalCount
            edges{
              node{
                status
              }
            }
          }
          futureMissions: missions(statusAndAbove:BOOKED statusAndBelow:CHECKED_IN) {
            totalCount
          }
        }
      }
    }
  }`,
    { cityId },
  )

/**
 * Gets all the commonly used data on the city dashboards
 */
const getCommonCityData = function(authToken, queryVars) {
  const query = `query ($cityId:ID!) {

    city(id: $cityId){
      id
      name
      location{
        latitude
        longitude
      }
      lunchCheckInTime
      afternoonCheckInTime
      h2hCheckInTimeBefore
      h2hCheckInTimeAfter
      h2hBonusCheckInTimeBefore
      h2hBonusCheckInTimeAfter
      d2dCheckInTimeBefore
      d2dCheckInTimeAfter
      d2dBonusCheckInTimeBefore
      d2dBonusCheckInTimeAfter
      payHourly
      recruitmentOnHold
      recruitmentCalendarId
      eventsCalendarId
      onboardingLocation
      onboardingUrl
      timezone
      statusV2
      cbdRadius
      pickupInformationURL
      checkinRadius
      newcomersMissionsLimitV2
      surgeInterval
      teamLeaderEnabled
      location {
        latitude
        longitude
      }
      country {
        code
        name
        id
      }
      clients {
        edges {
          node{
            id
            name
            logo
          }
        }
        totalCount
      }
      locations {
        edges {
          node {
            id
            name
            label
            moments{
              morning
              lunch
              afternoon
            }
            isSheltered
            hasPermit
            isEvent
            status
          }
        }
      }
      campaigns {
        edges {
          node {
            id
            name
            status
            client { id }
          }
        }
      }
      spies(statuses:[ACTIVE]){
          edges{
              node{
                id
                firstName
                lastName
                status
                schedule{
                  mondayMorning
                  mondayLunch
                  mondayAfternoon
                  tuesdayMorning
                  tuesdayLunch
                  tuesdayAfternoon
                  wednesdayMorning
                  wednesdayLunch
                  wednesdayAfternoon
                  thursdayMorning
                  thursdayLunch
                  thursdayAfternoon
                  fridayMorning
                  fridayLunch
                  fridayAfternoon
                  saturdayMorning
                  saturdayLunch
                  saturdayAfternoon
                  sundayMorning
                  sundayLunch
                  sundayAfternoon
                  }
              }
          }
      }
      spySettings{
        id
        cityId
        hourlyRate
        maxBudgetPerWeek
        travelAllowancePerMission
        travelAllowanceCap
        durationHandToHandMission
        durationLetterboxMission
        durationShift
        missionsPerShift
      }
      isOnsiteTrainingEnabled
    }
  }`
  return callGraphQLEndpoint(authToken, query, queryVars)
}

/**
 * Gets all the commonly used data on the city dashboards
 */
const getCityUsers = function(queryVars, queryUsers) {
  const query = `query (
    $cityId:ID!, 
    $includeInactiveWithInventory: Boolean, 
    $includeInactiveWithMissions: Boolean,
    $hasInventory: Boolean, 
    $hasMissionsBooked: Boolean,
    $statuses: [UserStatusEnum!]
    ) {
    city(id: $cityId){
      ${queryUsers}(
        hasInventory: $hasInventory,
        hasMissionsBooked: $hasMissionsBooked,
        statuses: $statuses,
        includeInactiveWithInventory: $includeInactiveWithInventory,
        includeInactiveWithMissions: $includeInactiveWithMissions
        ) {
        edges {
          node {
            id
            firstName
            lastName
            status
            role
          }
        }
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, queryVars)
}

/**
 * Gets all the countries
 */
const getCountries = function(authToken) {
  const query = `query {
    countries {
      nodes {
        id
        name,
        code,
        currencySign,
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query)
}

const getCountriesAuthorized = () => {
  const query = `query {
    countries {
      nodes {
        id
        name,
        code,
        currencySign,
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(query)
}

const getCountry = function(countryId) {
  const query = `query ($countryId: ID!) {
    country (id: $countryId) {
      id
      name,
      code,
      currencySign,
      currencyName,
      recruitmentChannels{
        selectedChannels
        customChannels
      },
      cities {
        edges{
          node {
            id
            name
            location {
              latitude
              longitude
            }
          }
        }
      }
      citiesSettings {
        nodes {
          id,
          name, 
          enabledChannelPreciseLocations,
          enabledChannelBroadAreas,
          enabledChannelLetterbox,
          h2hDistributionCosts,
          d2dDistributionCosts,
          bonusBa,
          baPayout,
          outfitCosts,
          permitCostsD2d,
          permitCostsH2h,
          travelCosts,
          minimumMissionLimit,
          weeklyMissionLimit,
          flyerPackH2h,
          flyerPackD2d,
          lunchCheckInTime,
          afternoonCheckInTime,
          timezone,
          clients {
            edges {
              node{
                id
                name
                logo
              }
            }
            totalCount
          },
          locations {
            edges {
              node {
                id
                name
                label
                moments{
                  morning
                  lunch
                  afternoon
                }
                isSheltered
                hasPermit
                isEvent
                status
              }
            }
          },
          campaigns {
            edges {
              node {
                id
                name
                status
                client { id }
              }
            }
          },
          spies(statuses:[ACTIVE]){
              edges{
                  node{
                    id
                    firstName
                    lastName
                    status
                    cityId
                    schedule{
                      mondayMorning
                      mondayLunch
                      mondayAfternoon
                      tuesdayMorning
                      tuesdayLunch
                      tuesdayAfternoon
                      wednesdayMorning
                      wednesdayLunch
                      wednesdayAfternoon
                      thursdayMorning
                      thursdayLunch
                      thursdayAfternoon
                      fridayMorning
                      fridayLunch
                      fridayAfternoon
                      saturdayMorning
                      saturdayLunch
                      saturdayAfternoon
                      sundayMorning
                      sundayLunch
                      sundayAfternoon
                      }
                  }
              }
          },
          spySettings{
            id
            cityId
            hourlyRate
            maxBudgetPerWeek
            travelAllowancePerMission
            travelAllowanceCap
            durationHandToHandMission
            durationLetterboxMission
            durationShift
            missionsPerShift
          }
        }
      },
      countryDefaultSettings {
        h2hDistributionCosts
        d2dDistributionCosts
        bonusBa
        baPayout
        outfitCosts
        permitCostsD2d
        permitCostsH2h
        travelCosts
        minimumMissionLimit
        weeklyMissionLimit
        flyerPackH2h
        flyerPackD2d
        countryTaxRate
        enabledChannelPreciseLocations
        enabledChannelBroadAreas
        enabledChannelLetterbox
        enabledChannelEddm
        enabledChannelAdm
        baPerLocation
        defaultCampaignLimits
      }
      printingCosts {
        nodes {
          id
          countryId
          label
          value
          flyersCount
          index
        }
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, {
    countryId,
  })
}

const getDMPrintingCosts = countryId => {
  const query = `query ($countryId: ID!) {
   getDMPrintingCosts(id: $countryId){
      DMPrintingCosts {
        countryId
        label
        value
        flyersCount
        version
        updatedAt
        updatedBy 
      } 
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, {
    countryId,
  })
}

const getADMPrintingCosts = countryId => {
  const query = `query ($countryId: ID!) {
   getADMPrintingCosts(id: $countryId){
      ADMPrintingCosts {
        countryId
        label
        value
        flyersCount
        version
        updatedAt
        updatedBy 
      } 
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, {
    countryId,
  })
}

const getStates = countryId => {
  const query = `query ($countryId: ID!) {
   getStates(id: $countryId){
      States {
        id
        countryId
        code
        name
        region
      } 
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, {
    countryId,
  })
}

const getPrinters = countryId => {
  const query = `query ($countryId: ID!) {
   getPrinters(countryId: $countryId){
      Printers {
        id
        countryId
        name
        stateId
        address
        zipcode
        bmeuZipcode
        city
        firstName
        lastName
        phoneNumber
        email
        updatedAt
        updatedBy
        availableStates
        crid
        permit
        latitude
        longitude
        mailerId
      } 
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, {
    countryId,
  })
}

const deletePrinter = id => {
  const mutation = `
    mutation deletePrinter ($id: ID!) {
      deletePrinter (id: $id) {
        success
        error
      }
    }
  `
  return callAuthorizedGraphQLEndpoint(mutation, {
    id,
  })
}

const createPrinter = input => {
  const mutation = `
    mutation createPrinter ($input: PrinterInput!) {
      createPrinter (input: $input) {
        Printer {
          id
        }
        error
      }
    }
  `
  return callAuthorizedGraphQLEndpoint(mutation, {
    input,
  })
}

const updatePrinter = input => {
  const mutation = `
    mutation updatePrinter ($input: PrinterInput!) {
      updatePrinter (input: $input) {
        Printer {
          id
        }
        error
      }
    }
  `
  return callAuthorizedGraphQLEndpoint(mutation, {
    input,
  })
}

/**
 * Get the upcoming pickup missions
 * @param {number} cityId To filter inventory by this city
 * @returns {Array<InventoryItem>}
 */
const getPickupMissionGraphQLFunc = `
  query getPickupMissionData(
    $cityId: ID!
    $now: String!
  ) {
    city(id:$cityId){
      lunchCheckInTime
      afternoonCheckInTime
      upcomingMissions: missions(statusAndAbove:BOOKED, statusAndBelow:BOOKED,startDate:$now){
        totalCount
        edges {
          node {
						id
            location {
              name
            }
            cityPlan {
							campaign {
                id
                name
                client {
                  id
                }
              }
            }
            dateTime
            user {
              id
              firstName
              lastName
              mobilePhone
              email
            }
            pickupDateTime
          }
        }
      }
      checkedInMissions: missions(statusAndAbove:CHECKED_IN, statusAndBelow:CHECKED_IN){
        totalCount
        edges {
          node {
						id
            cityPlan {
							campaign {
                name
                client {
                  id
                }
              }
            }
            dateTime
            user {
              id
              firstName
              lastName
              mobilePhone
              email
            }
            pickupDateTime
          }
        }
      }
    }
  }
`

/**
 * Get pickup missions data
 * @param {string} authToken
 * @param {object} params
 */
function getPickupMissionData(authToken, { cityId, now }) {
  return callGraphQLEndpoint(authToken, getPickupMissionGraphQLFunc, {
    cityId,
    now,
  })
}

const getPitchQuestions = ({ authToken, campaignId }) => {
  const query = `
    query ($campaignId: ID!){
      pitchQuestions (campaignId:$campaignId){
        id
        description
        type
        correctAnswer
        choices
      }
    }
  `
  const variables = { campaignId }
  return callGraphQLEndpoint(authToken, query, variables)
}

const setPitchQuestions = ({ authToken, campaignId, pitchQuestionsData }) => {
  const mutation = `
    mutation setPitchQuestions ($input: SetPitchQuestionsInput) {
      setPitchQuestions(input: $input){
        pitchQuestions {
          description
          type
          correctAnswer
          choices
        }
      }
    }
  `
  const variables = {
    input: {
      campaignId: Number(campaignId),
      pitchQuestions: pitchQuestionsData,
    },
  }
  return callGraphQLEndpoint(authToken, mutation, variables)
}

const unbookMission = (authToken, { missionId, unbookReason }) =>
  callGraphQLEndpoint(
    authToken,
    `
    mutation($input: UnbookMissionInput){
      unbookMission(input: $input){
        mission{
          id
          status
          user {
            id
          }
        }
      }
    }
  `,
    { input: { id: missionId, unbookReason } },
  )

const updateCityPlanGQL = (id, payload) => {
  const mutation = `
    mutation updateCityPlanSettings ($input: UpdateCityPlanInput) {
      updateCityPlanSettings(input: $input) {
        cityPlan {
          hourlyPayout
          clientMarginPercent
          distributionCostV2
          payout
        }
      }
    }
  `
  const {
    distributionCost,
    payout,
    hourlyPayout,
    clientMarginPercent,
  } = payload

  const variables = {
    input: {
      id,
      distributionCost: distributionCost,
      payout: payout,
      hourlyPayout: hourlyPayout,
      clientMarginPercent: clientMarginPercent,
    },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

/**
 * Notify missions successfully spied
 * @param {number[]} missionIds
 */
const notifySuccessfullySpiedMissions = missionIds =>
  callAuthorizedGraphQLEndpoint(
    `mutation notifySuccessfullySpiedMissions(
      $input: NotifySpiedMissionsInput
    ) {
      notifySuccessfullySpiedMissions(
        input: $input,
      ) { success }
    }`,
    {
      input: { missionIds },
    },
  )

const revertMissionDisqualification = ({ id }) => {
  return callAuthorizedGraphQLEndpoint(
    `mutation revertMissionDisqualification($input: RevertMissionDisqualificationInput!){
        revertMissionDisqualification(input: $input){
          success
      }
    }`,
    {
      input: { id },
    },
  )
}

/**
 * Gets all the data needed to edit and view a mission.
 */
const getMissionViewDataById = (id, includeLogs = false) => {
  const includeInventory = true
  return callAuthorizedGraphQLEndpoint(
    `
    query($id: ID!, $includeInventory: Boolean){
      mission(id: $id, includeInventory: $includeInventory){
        id
        status
        isNotShown
        cancelled
        type
        label
        bonusPerConversion
        bonusThreshold
        bonusDisqualified
        teamLeaderGroupId
        teamLeaderId
        user { 
          id
          firstName
          lastName
          profilePicture
          isTeamLeader
        }
        client { id name }
        campaign { 
          id 
          name 
          bonusEnabled
          type
          metrics  {
            nodes {
            id
            name
            label
            isPrimary 
           }
          }
        }
        location { 
          id
          name
          typeId
          longitude
          latitude
          isSelfServe
        }
        cityPlan {
          id
          city { 
            id
            name
            timezone
            morningCheckInTime
            lunchCheckInTime
            afternoonCheckInTime
            h2hCheckInTimeBefore
            h2hCheckInTimeAfter
            h2hBonusCheckInTimeBefore
            h2hBonusCheckInTimeAfter
            d2dCheckInTimeBefore
            d2dCheckInTimeAfter
            d2dBonusCheckInTimeBefore
            d2dBonusCheckInTimeAfter
            teamLeaderEnabled
            country {
              code
              currencySign
            }
            locations{
              edges {
                node {
                  id
                  name
                  longitude
                  latitude
                  isSelfServe
                  status
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  statusV2
                  firstName
                  lastName
                  isTeamLeader
                }
              }
            }
          }
        }
        totalFlyersDistributed
        flyerCodeV2 {
          id
          code
          quantity
        }
        checkInTime
        checkInSelfie
        checkInFlyerCodeImage
        checkInFlyerPackImage
        checkInLocation {
          latitude
          longitude
        }
        checkoutTime
        checkoutFeedback
        checkoutStepsWalked
        checkoutLocation {
          latitude
          longitude
        }
        totalFlyersDistributed
        payoutRatio
        payoutRatioComment
        pickupDateTime
        draftPayAdjustment
        comments
        timeRangeStart
        recommendedCheckInTime { from }
        notifiedSuccessfullySpied
        dateTime
        distributionCostV2
        printingCostPerFlyer
        draftPayAdjustment
        printingCost
        conversions {
          nodes {
            id
            timestamp
            metric { id label name }
          }
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        flyerCodes {
          edges {
            node {
              id
              quantity
              code
            }
          }
        }
        activities {
          edges {
            node {
              dateTime
              position {
                latitude
                longitude
              }
            }
          }
        }
        costPerFlyer
        amountScans
        canBeSpied
        spyInformation {
          spy {
            id
            firstName
            lastName
            status
          }
          spyDetails {
            checkInTime
            checkoutTime
            reportReason
            photoUrl
          }
        }
        isAnonymize
        ${
          includeLogs
            ? `
        logs
        `
            : ''
        }
      }
    }
  `,
    { id, includeInventory },
  )
}

/**
 * Create missions
 * @param {Array<Object>} args.mission
 */
const createMissions = async ({ missions, onProgress, campaignId }) => {
  const mutation = `
  mutation createMissions ($input: CreateMissionsInput!) {
    createMissions(input: $input) {
      missions { 
        id
        status
        dateTime
        distributionCost
        costPerFlyer
        draftPayAdjustment
        printingCostPerFlyer
        bonusPerConversion
        bonusRedemptionPeriod
        type
        label
        location {
          id
          name
          hasPermit
          rank
          detailedRank
          type {
            name
          }
        }
        cityPlan { 
          id 
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        amountScans
        campaign { id, name }
      }
    }
  }
`
  const itemsPerChunk = 100

  const chunks = splitArrayIntoChunks({
    inputArray: missions,
    itemsPerChunk,
  })
  let createdMissions = []

  for (let index = 0; index < chunks.length; index++) {
    const missionsInChunk = chunks[index]
    const variables = {
      input: {
        missions: missionsInChunk,
        relatedCampaign: +campaignId || null,
      },
    }
    const req = await callAuthorizedGraphQLEndpoint(mutation, variables, {
      ignoreErrors: true,
    })
    const result = await req.json()
    createdMissions = [
      ...createdMissions,
      ...((result.data.createMissions && result.data.createMissions.missions) ||
        []),
    ]
    if (onProgress) {
      onProgress({
        itemsPerChunk,
        processedAmount: createdMissions.length,
        totalAmount: missions.length,
      })
    }
  }

  // const results = await Promise.all(
  //   chunks.map(async missions => {
  //     const variables = {
  //       input: { missions },
  //     }
  //     const req = await callAuthorizedGraphQLEndpoint(mutation, variables)
  //     return req.json()
  //   }),
  // )

  // const createdMissions = results.reduce((acc, result) => {
  //   acc = [...acc, ...result.data.createMissions.missions]
  //   return acc
  // }, [])

  return createdMissions
}

const importMissionsMasterSchedule = async ({
  missions,
  onProgress,
  campaignId,
}) => {
  const mutation = `
  mutation importMissionMasterSchedule ($input: CreateMissionsInput!) {
    importMissionMasterSchedule(input: $input) {
      missions { 
        id
        status
        dateTime
        distributionCost
        costPerFlyer
        draftPayAdjustment
        printingCostPerFlyer
        bonusPerConversion
        bonusRedemptionPeriod
        type
        label
        location {
          id
          name
          hasPermit
          rank
          detailedRank
          type {
            name
          }
        }
        cityPlan { 
          id 
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        amountScans
        campaign { id, name }
      }
    }
  }
`
  const itemsPerChunk = 20

  const chunks = splitArrayIntoChunks({
    inputArray: missions,
    itemsPerChunk,
  })
  let importedMissions = []
  if (onProgress && !importedMissions.length) {
    onProgress({
      itemsPerChunk,
      processedAmount: 0,
      totalAmount: 100,
    })
  }

  for (let index = 0; index < chunks.length; index++) {
    const missionsInChunk = chunks[index]
    const variables = {
      input: {
        missions: missionsInChunk,
        relatedCampaign: +campaignId || null,
      },
    }
    const request = await callAuthorizedGraphQLEndpoint(mutation, variables, {
      ignoreErrors: false,
    })

    if (request.status === 200) {
      const result = await request.json()
      importedMissions = [
        ...importedMissions,
        ...((result.data.importMissionMasterSchedule &&
          result.data.importMissionMasterSchedule.missions) ||
          []),
      ]
      if (onProgress) {
        onProgress({
          itemsPerChunk,
          processedAmount: importedMissions.length,
          totalAmount: missions.length,
        })
      }
    } else {
      return { importedMissions, isFailed: true }
    }
  }

  return { importedMissions, isFailed: false }
}

const updateMission = ({ missionInput }, options) => {
  const mutation = `
  mutation updateMission ($input: UpdateMissionInput!) {
    updateMission(input: $input) {
      mission { 
        id
        status
        type
        label
        user { 
          id
          firstName
          lastName
          profilePicture
          isTeamLeader
        }
        client { id name }
        campaign { id name }
        location { 
          id
          name
          longitude
          latitude
        }
        cityPlan {
          id
          city { 
            id
            name
            timezone
            lunchCheckInTime
            afternoonCheckInTime
            locations{
              edges {
                node {
                  id
                  name
                  longitude
                  latitude
                  isSelfServe
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
          }
        }
        totalFlyersDistributed
        flyerCodeV2 {
          id
          code
          quantity
        }
        checkInTime
        checkInSelfie
        checkInFlyerCodeImage
        checkInFlyerPackImage
        checkInLocation {
          latitude
          longitude
        }
        checkoutTime
        checkoutStepsWalked
        checkoutLocation {
          latitude
          longitude
        }
        totalFlyersDistributed
        payoutRatio
        payoutRatioComment
        pickupDateTime
        draftPayAdjustment
        comments
        timeRangeStart
        recommendedCheckInTime { from }
        notifiedSuccessfullySpied
        dateTime
        bonusPerConversion
        distributionCostV2
        printingCostPerFlyer
        draftPayAdjustment
        printingCost
        bonusThreshold
        conversions {
          nodes {
            id
            timestamp
            metric { id name }
          }
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        flyerCodes {
          edges {
            node {
              id
              quantity
              code
            }
          }
        }
        canBeSpied
        spyInformation {
          spyDetails {
            checkInTime
          }
        }
      }
    }
  }
`

  const variables = {
    input: missionInput,
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables, options)
}

const updateMissionSimplified = ({ missionInput }, options) => {
  const mutation = `
  mutation updateMission ($input: UpdateMissionInput!) {
    updateMission(input: $input) {
      mission { 
        id
      }   
    }
  }
`

  const variables = {
    input: missionInput,
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables, options)
}

export const createCampaign = payload =>
  callAuthorizedGraphQLEndpoint(
    `mutation createCampaign(
    $input: CreateCampaignInput
  ) {
    createCampaign(
      input: $input,
    ) { 
      campaign { 
        id 
        type
        name
      }
    }
  }`,
    {
      input: {
        ...payload,
        // legacy prop that wants the input in dollars
        printingCostPerFlyer: payload.printingCostPerFlyer
          ? payload.printingCostPerFlyer / 100
          : payload.printingCostPerFlyer,
      },
    },
  )

const updateCampaign = payload => {
  return callAuthorizedGraphQLEndpoint(
    `mutation updateCampaign(
    $input: UpdateCampaignInput
  ) {
    updateCampaign(
      input: $input,
    ) { 
      campaign { id }
    }
  }`,
    {
      input: {
        ...payload,
        // legacy prop that wants the input in dollars
        printingCostPerFlyer: payload.printingCostPerFlyer
          ? payload.printingCostPerFlyer / 100
          : payload.printingCostPerFlyer,
      },
    },
  )
}

const getCampaignById = (
  campaignId,
  { includeMissions, includeMissionsCount } = {},
) =>
  callAuthorizedGraphQLEndpoint(
    `query ($campaignId:ID!) {
      campaign(id:$campaignId){
        id
        name
        pitchUrl
        type
        subtype
        status
        workflow {
          id
          name
          uuid
        }
        printingCostPerFlyer: printingCostPerFlyerV2 #Get it in cents
        bonusEnabled
        bonusLimit
        payoutPerConversion
        baRedemptionPeriod
        distributionCost
        basePayout
        receiveReport
        trackingType
        goal
        metrics {
          nodes {
            id
            label
            name
            targetConversionRate
            targetCostPerMetric
            isPrimary
          }
        }
        targeting {
          type
          gender
          income
          ageGroups
          applyNiche
          nicheDescription
        }
        artworksLean {
          id
          name
          inventoryCount
        }
        selfServeData {
          lastActiveStep
          internalStatus
          isPaymentSkipped
          paymentSkipperId
          paymentSkippedBy
          couponType
          dynamicCouponCode
          staticCouponCode
        }
        isSelfServe
        client {
          id
          logo
        }
        country {
          id
          code
        }
        ${
          includeMissionsCount
            ? `
        cityPlans {
          nodes {
            id
            city { 
              id
              name
            }
            missions {
              totalCount
            }
          }
        }`
            : ''
        }
        ${
          includeMissions
            ? `
        cityPlans {
          nodes {
            id
            city { 
              id
              name
            }
            missions {
              edges {
                node {
                  id,
                  boundaryColour,
                  dateTime,
                  status,
                  label,
                  boundary {
                    latitude
                    longitude
                  }
                }
              }
            }
          }
        }`
            : ''
        }
        startDate
        campaignDurationWeek
        offerExpirationDate
        noExpiration
        conversionsApiEnabled
        scanDataEnabled
        isFlyerCodeRequired
        bonusThreshold
        purchaseOrderNumber
      }
    }`,
    { campaignId },
  )

/**
 * Gets all the Campaigns
 */
const getCampaigns = ({ statuses } = { statuses: 'ACTIVE' }) => {
  const query = `
    query ($statuses:[CampaignStatusEnum!]) {
      campaigns (statuses: $statuses) {
        edges {
          node {
            id
            name
            pitchUrl
            type
            status
            countryId
            conversionReportType
            metrics {
              nodes {
                id
                name
                label
                targetConversionRate
                targetCostPerMetric
                isPrimary
              }
            }
            artworksLean {
              id
              name
            }
            cityPlans {
              nodes{
                id
                city {
                  id
                  name
                  statusV2
                }
              }
            }
            client {
              id
              name
              logo
            }
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query, { statuses })
}

/**
 * Gets all the Cities
 */
const getCities = () => {
  const query = `
    query {
      cities {
        edges {
          node {
            id
            name
            lunchCheckInTime
            afternoonCheckInTime
            h2hCheckInTimeBefore
            h2hCheckInTimeAfter
            h2hBonusCheckInTimeBefore
            h2hBonusCheckInTimeAfter
            d2dCheckInTimeBefore
            d2dCheckInTimeAfter
            d2dBonusCheckInTimeBefore
            d2dBonusCheckInTimeAfter
            payHourly
            recruitmentOnHold
            recruitmentCalendarId
            onboardingLocation
            onboardingUrl
            timezone
            statusV2
            cbdRadius
            pickupInformationURL
            checkinRadius
            newcomersMissionsLimitV2
            surgeInterval
            teamLeaderEnabled
            location {
              latitude
              longitude
            }
            country {
              id
              name
              code
            }
            salesTaxRate
            state
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query)
}

const createCityPlan = cityPlan => {
  const mutation = `mutation createCityPlan($input: CreateCityPlanInput){
      createCityPlan(input: $input){
            cityPlan {
              id
              hourlyPayout
              clientMarginPercent
              payout
              distributionCost
        }
      }
    }`

  const variables = {
    input: { ...cityPlan },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

const createCityPlans = cityPlans => {
  const mutation = `
    mutation createCityPlans ($input: CreateCityPlansInput!) {
      createCityPlans(input: $input) {
        cityPlans { 
          id
          city {
            id
          }
        }
      }
    }`

  const variables = {
    input: { cityPlans },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

const getCityPlans = options => {
  const query = `query($campaignId:ID, $cityId:ID) {
    cityPlans(campaignId: $campaignId, cityId: $cityId) {
      edges {
        node {
          id
          city {
            id
          }
        }
      }
    }
  }`

  return callAuthorizedGraphQLEndpoint(query, options)
}

const getClients = ({
  statuses = ['ACTIVE'],
  countryIds,
  first,
  skip,
} = {}) => {
  const query = `
  query(
    $statuses:[ClientStatusEnum], 
    $countryIds: [ID], 
    $first: Int, 
    $skip: Int
  ) {
    clients(
      statuses: $statuses, 
      countryIds: $countryIds, 
      first: $first, 
      skip: $skip
    ) {
      edges{
        cursor
        node {
          id
          name
          logo
          status
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }`

  return callAuthorizedGraphQLEndpoint(query, {
    statuses,
    countryIds,
    first,
    skip,
  })
}

/**
 * Grabs the mission activity records from the API project.
 * Mission activities are now stored in Trailblazer, hence this is a legacy
 * method, however it needs to be used to view historic data.
 */
const getMissionActivitiesLegacy = id => {
  const query = `
    query($id: ID!){
      mission(id: $id){
        activities {
          edges {
            node {
              dateTime
              position {
                latitude
                longitude
              }
            }
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query, { id })
}

/**
 * Grabs the mission activity records.
 */
const getMissionActivities = ({
  users,
  from,
  to,
  after,
  missions,
  onlyLastPoint,
}) => {
  const query = `
    query($dateRange: DateRangeSearch, $after: String, $users: [ID!], $missions: [ID!], $onlyLastPoint: Boolean) {
      searchFeatures(dateRange: $dateRange, after: $after, users: $users, missions: $missions, onlyLastPoint: $onlyLastPoint){
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        collection {
          type
          features {
            type
            properties
            geometry {
              type
              coordinates
            }
          }
        }
      }  
    }
  `
  return callTrailblazer(query, {
    from,
    to,
    after,
    users,
    missions,
    onlyLastPoint,
  })
}

const fetchCityPlanMissionFormChunks = async ({
  gql,
  cityPlanId,
  chunkSize = 200,
  skip = 0,
  ...params
}) => {
  const includeInventory = true
  const res = await callAuthorizedGraphQLEndpoint(
    gql,
    {
      cityPlanId,
      first: chunkSize,
      skip,
      includeInventory,
      ...params,
    },
    { getCache: true },
  )

  const { data } = await res.json()

  let missionsResult = data.cityPlans.edges[0].node.missions.edges

  if (data.cityPlans.edges[0].node.missions.pageInfo.hasNextPage) {
    const { missions } = await fetchCityPlanMissionFormChunks({
      cityPlanId,
      chunkSize,
      skip: chunkSize + skip,
      gql,
      ...params,
    })

    missionsResult = [...missionsResult, ...missions]
  }
  return {
    missions: missionsResult,
  }
}

const getCampaignByChunks = async ({
  campaignId,
  cityPlanChunk,
  missionChunk,
  missionStatuses,
  queries,
}) => {
  const { gqlCampaign, gqlCityPlans, gqlMissions } = queries
  const campaignResponse = await callAuthorizedGraphQLEndpoint(
    gqlCampaign,
    {
      campaignId,
    },
    { getCache: true },
  )

  const campaign = await campaignResponse.json()

  const campaignResult = campaign.data.campaign
  const cityPlansIds = campaign.data.campaign.cityPlans.nodes
    ? campaign.data.campaign.cityPlans.nodes.map(({ id }) => id)
    : []

  const cityPlans = await iteratePromisesInChunks(
    cityPlansIds,
    cityPlanChunk,
    async cityPlanId => {
      const cityPlanResponse = await callAuthorizedGraphQLEndpoint(
        gqlCityPlans,
        {
          cityPlanId,
        },
        { getCache: true },
      )

      const { missions } = await fetchCityPlanMissionFormChunks({
        cityPlanId,
        chunkSize: missionChunk,
        skip: 0,
        gql: gqlMissions,
        missionStatuses,
      })

      const resCityPlanResponse = await cityPlanResponse.json()

      const cityPlan = resCityPlanResponse.data.cityPlans.edges[0].node

      return {
        ...cityPlan,
        missions: {
          edges: missions,
        },
      }
    },
  )

  return { ...campaignResult, cityPlans: { nodes: cityPlans } }
}

const getPitchUrl = async ({ type, campaignId, cityId }) => {
  const query = `
    query ($type: String!, $campaignId: ID, $cityId: ID) {
      quiz (type: $type, campaignId: $campaignId, cityId: $cityId) {
        url
        questionCount
      }
    }
  `
  const variables = { type, campaignId, cityId }

  return await callAuthorizedGraphQLEndpoint(query, variables)
}

const getQuiz = async ({ type, campaignId, cityId, userId }) => {
  const query = `
    query ($type: String!, $campaignId: ID, $cityId: ID, $userId: ID) {
      quiz (type: $type, campaignId: $campaignId, cityId: $cityId, userId: $userId) {
        id
        campaignId
        cityId
        type
        url
        isFinished
        weekly
        amountOfQuestions
        quizQuestions {
          id
          typeChoice
          question
          quizAnswers {
            id
            answer
            isRight
          }
        }
      }
    }
  `
  const variables = { type, campaignId, cityId, userId }

  return await callAuthorizedGraphQLEndpoint(query, variables)
}

const setQuiz = async ({
  type,
  campaignId,
  cityId,
  userId,
  url,
  quizQuestions,
  amountOfQuestions,
}) => {
  const query = `
    mutation ($input: QuizInput) {
      setQuiz (input: $input) {
        success
      }
    }
  `
  const variables = {
    input: {
      type,
      campaignId,
      cityId,
      userId,
      url,
      quizQuestions,
      amountOfQuestions,
    },
  }

  return await callAuthorizedGraphQLEndpoint(query, variables)
}

const updateQuiz = async ({ id, weekly }) => {
  const query = `
    mutation ($input: UpdateQuizInput) {
      updateQuiz (input: $input) {
        success
      }
    }
  `
  const variables = {
    input: {
      id,
      weekly,
    },
  }

  return await callAuthorizedGraphQLEndpoint(query, variables)
}
const duplicateQuiz = async ({
  type,
  campaignId,
  cityId,
  userId,
  url,
  quizQuestions,
  amountOfQuestions,
}) => {
  const query = `
    mutation ($input: QuizInput) {
      duplicateQuiz (input: $input) {
        success
      }
    }
  `
  const variables = {
    input: {
      type,
      campaignId,
      cityId,
      userId,
      url,
      quizQuestions,
      amountOfQuestions,
    },
  }

  return await callAuthorizedGraphQLEndpoint(query, variables)
}

/**
 * Gets all the commonly used data on the city dashboards
 */
const getCityCoords = async cityId => {
  const query = `query ($cityId:ID!) {
    city(id: $cityId){
      id
      name
      location{
        latitude
        longitude
      }
    }
  }`
  const res = await callAuthorizedGraphQLEndpoint(query, { cityId })
  return res.json()
}

const getStatesTaxesForUS = async () => {
  const query = `
    query {
      statesTax {
        name
        code
      }
    }`
  const res = await callAuthorizedGraphQLEndpoint(query)
  const {
    data: { statesTax },
  } = await res.json()

  return statesTax
}

const updateMissionNormalized = ({ missionInput }, options) => {
  const mutation = `
  mutation updateMission ($input: UpdateMissionInput!) {
    updateMission(input: $input) {
      mission { 
        id
        status
        type
        label
        user { 
          id
          firstName
          lastName
          profilePicture
          isTeamLeader
        }
        client { id name }
        campaign { id name }
        location { 
          id
          name
          longitude
          latitude
        }
        cityPlan {
          id
          city { 
            id
            name
            timezone
            lunchCheckInTime
            afternoonCheckInTime
          }
        }
        totalFlyersDistributed
        flyerCodeV2 {
          id
          code
          quantity
        }
        checkInTime
        checkInSelfie
        checkInFlyerCodeImage
        checkInFlyerPackImage
        checkInLocation {
          latitude
          longitude
        }
        checkoutTime
        checkoutStepsWalked
        checkoutLocation {
          latitude
          longitude
        }
        totalFlyersDistributed
        payoutRatio
        payoutRatioComment
        pickupDateTime
        draftPayAdjustment
        comments
        timeRangeStart
        recommendedCheckInTime { from }
        notifiedSuccessfullySpied
        dateTime
        bonusPerConversion
        distributionCostV2
        printingCostPerFlyer
        draftPayAdjustment
        printingCost
        bonusThreshold
        conversions {
          nodes {
            id
            timestamp
            metric { id name }
          }
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        canBeSpied
        spyInformation {
          spyDetails {
            checkInTime
          }
        }
      }
    }
  }
`

  const variables = {
    input: missionInput,
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables, options)
}

const migrateUser = ({ userId, cityId }) => {
  const query = `
    mutation ($input: MigrateUserInput!) {
      migrateUser(input: $input) {
        id
        statusV2
        city {
          id
          name
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query, { input: { userId, cityId } })
}

/**
 * Power banks. Every call below is a city-manager operation scoped to the
 * manager's locked city by the API; the platform still sends the city it is
 * looking at so a stale screen fails loudly instead of acting elsewhere.
 *
 * `doNotShowErrorModal` is set on all of them because each screen shows its
 * own success / error message next to the button that was pressed.
 */
const POWER_BANK_CALL_OPTIONS = { doNotShowErrorModal: true }

const POWER_BANK_HOLD_FIELDS = `
  id
  inventoryId
  description
  amount
  currency
  status
  comments
  placedAt
  resolvedAt
  acceptedAt
  ambassador { id }
`

/**
 * The currency sign of the country a city belongs to. Used to render hold
 * amounts with the city's own sign (a hold in Tel Aviv is in shekels).
 * @param {string|number} cityId
 */
const getCityCurrency = cityId => {
  const query = `query ($cityId: ID!) {
    city(id: $cityId) {
      id
      country {
        code
        currencySign
        currencyName
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityId },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * A city's default answer to "hold for this item? Y/N" and "how much?".
 * @param {string|number} cityId
 */
const getPowerBankHoldSetting = cityId => {
  const query = `query ($cityId: ID!) {
    powerBankHoldSetting(cityId: $cityId) {
      city { id }
      itemTypeId
      holdRequired
      holdAmount
      currency
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityId },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Save a city's default power bank hold.
 * @param {object} input
 * @param {string|number} input.cityId
 * @param {boolean} input.holdRequired
 * @param {number} input.holdAmount
 */
const setPowerBankHoldSetting = ({ cityId, holdRequired, holdAmount }) => {
  const query = `mutation ($input: SetPowerBankHoldSettingInput!) {
    setPowerBankHoldSetting(input: $input) {
      city { id }
      itemTypeId
      holdRequired
      holdAmount
      currency
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityId, holdRequired, holdAmount } },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Bulk create power bank units for a city, one Inventory row per unit.
 * The API caps this at 200 units per call.
 * @param {object} input
 * @param {string|number} input.cityId
 * @param {string|number} input.clientId
 * @param {Array<{serialNumber: ?string, comments: ?string}>} input.units
 */
const addPowerBankUnits = ({ cityId, clientId, units }) => {
  const query = `mutation ($input: AddPowerBankUnitsInput!) {
    addPowerBankUnits(input: $input) {
      createdCount
      units {
        id
        description
        itemTypeId
        client { id }
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityId, clientId, units } },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Hand a power bank to a brand ambassador. `holdAmount` null means "use the
 * city default", a number overrides it for this one handover (0 records the
 * transfer with no money held).
 * @param {object} input
 */
const assignPowerBank = ({
  inventoryId,
  ambassadorId,
  cityId,
  holdAmount,
  comments,
}) => {
  const query = `mutation ($input: AssignPowerBankInput!) {
    assignPowerBank(input: $input) {
      inventoryId
      description
      hold { ${POWER_BANK_HOLD_FIELDS} }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: {
        inventoryId,
        ambassadorId,
        cityId,
        holdAmount: holdAmount === undefined ? null : holdAmount,
        comments: comments || null,
      },
    },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Take a power bank back from a brand ambassador and release their hold.
 * @param {object} input
 */
const returnPowerBank = ({ inventoryId, ambassadorId, cityId, comments }) => {
  const query = `mutation ($input: ReturnPowerBankInput!) {
    returnPowerBank(input: $input) {
      inventoryId
      description
      hold { ${POWER_BANK_HOLD_FIELDS} }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: { inventoryId, ambassadorId, cityId, comments: comments || null },
    },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Write off a lost or broken power bank. The unit is retired and, if there
 * is a hold, the company keeps the money.
 * @param {object} input
 */
const writeOffPowerBank = ({ inventoryId, ambassadorId, cityId, comments }) => {
  const query = `mutation ($input: WriteOffPowerBankInput!) {
    writeOffPowerBank(input: $input) {
      inventoryId
      description
      hold { ${POWER_BANK_HOLD_FIELDS} }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: { inventoryId, ambassadorId, cityId, comments: comments || null },
    },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * The power banks currently out with a brand ambassador, their holds and the
 * totals across those holds.
 * @param {object} args
 * @param {string|number} args.ambassadorId
 * @param {string|number} [args.cityId]
 * @param {?string} [args.holdStatus] null for the full hold history
 */
const getAmbassadorPowerBanks = ({ ambassadorId, cityId, holdStatus }) => {
  const query = `query ($ambassadorId: ID!, $cityId: ID, $holdStatus: String) {
    ambassadorPowerBanks(
      ambassadorId: $ambassadorId
      cityId: $cityId
      holdStatus: $holdStatus
    ) {
      units {
        inventoryId
        description
        city { id }
        quantityOut
        holdId
        holdAmount
        holdCurrency
        holdStatus
      }
      holds { ${POWER_BANK_HOLD_FIELDS} }
      totals {
        heldAmount
        releasedAmount
        cancelledAmount
        activeCount
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { ambassadorId, cityId, holdStatus },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * Every live power bank hold in a city, for the pickup page's amount and
 * "agreed / awaiting agreement" labels.
 * @param {string|number} cityId
 */
const getCityPowerBankHolds = cityId => {
  const query = `query ($cityId: ID!) {
    cityPowerBankHolds(cityId: $cityId) { ${POWER_BANK_HOLD_FIELDS} }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityId },
    POWER_BANK_CALL_OPTIONS,
  )
}

/**
 * ---- City plan pages (server-paginated) ----------------------------------
 * These back the letterbox City Plan routes. All take `cityPlanId` and never
 * return a mission's polygon except fetchCityPlanMissionBoundaries.
 */
const CITY_PLAN_CALL_OPTIONS = { doNotShowErrorModal: true }

const CITY_PLAN_FILTER_VARS = `$filter: CityPlanMissionFilterInput`

const MISSION_ROW_FIELDS = `
  id
  label
  status
  type
  isReadyToPublish
  dateTime
  isoWeekKey
  userId
  user { id firstName lastName }
  draftPayAdjustment
  payout
  surgePayout
  distributionCost
  costPerFlyer
  publicationDate
  boundaryColour
  meetingPoint
  automatic
  flyerInventoryId
  totalFlyersDistributed
  householdsAmount
  areaKm2
  density
  hasBoundary
`

const BULK_RESULT_FIELDS = `
  matchedCount
  updatedCount
  skipped { missionId reason }
  job { id type status total processed failed error }
`

/**
 * One page of a city plan's missions.
 * @param {object} args {cityPlanId, first, skip, filter, orderBy}
 */
const fetchCityPlanMissionsPage = ({
  cityPlanId,
  first,
  skip,
  filter,
  orderBy,
}) => {
  const query = `query ($cityPlanId: ID!, $first: Int, $skip: Int, ${CITY_PLAN_FILTER_VARS}, $orderBy: CityPlanMissionOrderByEnum) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges { node {
        id
        missionsPage(first: $first, skip: $skip, filter: $filter, orderBy: $orderBy) {
          totalCount
          pageInfo { hasNextPage endCursor }
          nodes { ${MISSION_ROW_FIELDS} }
        }
      } }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityPlanId, first, skip, filter, orderBy },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * Totals, per-status and per-week counts of a city plan.
 * @param {object} args {cityPlanId, filter?}
 */

const CLUSTER_MISSION_FIELDS = `
  id
  label
  status
  userId
  isStandby
  standbyOutcome
  standbyCoveredMissionId
  checkInTime
  hasBoundary
`

/**
 * Every shift of a city plan: one row per meeting point with its counts.
 * @param {object} args {cityPlanId, from, to}
 */
const fetchMissionClusters = ({ cityPlanId, from, to }) => {
  const query = `query ($input: MissionClustersInput!) {
    missionClusters(input: $input) {
      meetingPoint
      missionCount
      bookedCount
      checkedInCount
      standbyCount
      standbyBookedCount
      coveredCount
      releasedCount
      noShowCount
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, from: from || null, to: to || null } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * One shift: its missions and the standby BAs booked on top of it.
 * @param {object} args {cityPlanId, meetingPoint}
 */
const fetchMissionCluster = ({ cityPlanId, meetingPoint }) => {
  const query = `query ($input: MissionClusterInput!) {
    missionCluster(input: $input) {
      meetingPoint
      openStandbyCount
      missions { ${CLUSTER_MISSION_FIELDS} }
      standby { ${CLUSTER_MISSION_FIELDS} }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: {
        cityPlanId,
        meetingPoint: {
          address: meetingPoint.address,
          dateTime: meetingPoint.dateTime,
        },
      },
    },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * A campaign's overbooking settings.
 * @param {object} args {campaignId}
 */
const fetchCampaignStandbySettings = ({ campaignId }) =>
  callAuthorizedGraphQLEndpoint(
    `query ($campaignId: ID!) {
      campaignStandbySettings(campaignId: $campaignId) {
        campaignId standbyPercent standbyNoticeEnabled standbyWorkChance standbyNoticeTexts standbyReleaseHours standbyReleasePercent
      }
    }`,
    { campaignId },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * Saves a campaign's overbooking settings; the API applies the percentage to
 * every shift of the campaign's plans.
 * @param {object} input {campaignId, standbyPercent, standbyNoticeEnabled, standbyWorkChance}
 */
const updateCampaignStandbySettings = input =>
  callAuthorizedGraphQLEndpoint(
    `mutation ($input: UpdateCampaignStandbySettingsInput!) {
      updateCampaignStandbySettings(input: $input) {
        settings { campaignId standbyPercent standbyNoticeEnabled standbyWorkChance standbyNoticeTexts standbyReleaseHours standbyReleasePercent }
        shifts created removed
      }
    }`,
    { input },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * Copies every ordinary mission of one day of a plan to an empty day, as drafts.
 * @param {object} input {cityPlanId, fromDay, toDay}
 */
const duplicateCityPlanDay = input =>
  callAuthorizedGraphQLEndpoint(
    `mutation ($input: DuplicateCityPlanDayInput!) {
      duplicateCityPlanDay(input: $input) { copied standbyCreated fromDay toDay }
    }`,
    { input },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * The standby BAs on the same shift as a mission, for the swap list.
 * @param {object} args {missionId}
 */
const fetchMissionSwapCandidates = ({ missionId }) =>
  callAuthorizedGraphQLEndpoint(
    `query ($missionId: ID!) {
      missionSwapCandidates(missionId: $missionId) {
        userId name standbyMissionId checkedIn
      }
    }`,
    { missionId },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * Swaps the BA on a mission: a checked in standby covers it, anyone else takes it over.
 * @param {object} input {missionId, userId}
 */
const swapMissionAmbassador = input =>
  callAuthorizedGraphQLEndpoint(
    `mutation ($input: SwapMissionAmbassadorInput!) {
      swapMissionAmbassador(input: $input) { action mission { id label userId } }
    }`,
    { input },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * Sends a standby home on half pay.
 * @param {object} args {missionId}
 */
const sendStandbyHome = ({ missionId }) =>
  callAuthorizedGraphQLEndpoint(
    `mutation ($missionId: ID!) {
      sendStandbyHome(missionId: $missionId) { id standbyOutcome }
    }`,
    { missionId },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * A city's spy report options. Null means the app's own list.
 * @param {object} args {cityId}
 */
const fetchCitySpyReportOptions = ({ cityId }) =>
  callAuthorizedGraphQLEndpoint(
    `query ($cityId: ID!) {
      city(id: $cityId) { id spySettings { id reportOptions } }
    }`,
    { cityId },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * Saves a city's spy report options; null goes back to the app's list.
 * @param {object} args {cityId, options}
 */
const updateCitySpyReportOptions = ({ cityId, options }) =>
  callAuthorizedGraphQLEndpoint(
    `mutation ($cityId: ID!, $options: JSON) {
      updateCitySpyReportOptions(cityId: $cityId, options: $options) {
        cityId reportOptions
      }
    }`,
    { cityId, options },
    CITY_PLAN_CALL_OPTIONS,
  )

/**
 * The active brand ambassadors of a city, just enough to pick one.
 * @param {object} args {cityId}
 */
const fetchCityAmbassadors = ({ cityId }) => {
  // City.brandAmbassadors is the paged list the BA page uses. The root level
  // brandAmbassadors field loads documents, inventory and missions for up to
  // 500 BAs and timed out, which left the picker silently empty.
  const query = `query ($cityId: ID!) {
    city(id: $cityId) {
      brandAmbassadors(statuses: [ACTIVE], first: 2000, orderBy: NAME_ASC) {
        edges { node { id firstName lastName } }
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityId },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * Gives every shift of a plan the same number of standby BAs.
 * @param {object} args {cityPlanId, count, from, to}
 */
const setStandbySlotsForPlan = ({ cityPlanId, count, percent, from, to }) => {
  const query = `mutation ($input: SetStandbySlotsForPlanInput!) {
    setStandbySlotsForPlan(input: $input) { shifts created removed }
  }`
  const input = { cityPlanId, from: from || null, to: to || null }
  if (percent !== undefined && percent !== null) input.percent = percent
  else input.count = count
  return callAuthorizedGraphQLEndpoint(query, { input }, CITY_PLAN_CALL_OPTIONS)
}

/**
 * Books a whole shift onto one BA, without publishing it to everybody.
 * @param {object} args {cityPlanId, meetingPoint, userId, includeStandby}
 */
const bookShiftForUser = ({
  cityPlanId,
  meetingPoint,
  userId,
  includeStandby = true,
}) => {
  const query = `mutation ($input: BookShiftForUserInput!) {
    bookShiftForUser(input: $input) { booked standbyBooked published }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: {
        cityPlanId,
        meetingPoint: {
          address: meetingPoint.address,
          dateTime: meetingPoint.dateTime,
        },
        userId,
        includeStandby,
      },
    },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * Sets how many standby BAs a shift carries. Slots nobody booked are removed.
 * @param {object} args {cityPlanId, meetingPoint, count}
 */
const setStandbySlots = ({ cityPlanId, meetingPoint, count }) => {
  const query = `mutation ($input: SetStandbySlotsInput!) {
    setStandbySlots(input: $input) { created removed slots }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    {
      input: {
        cityPlanId,
        meetingPoint: {
          address: meetingPoint.address,
          dateTime: meetingPoint.dateTime,
        },
        count,
      },
    },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * Hands a mission over to a standby because its BA did not show.
 * @param {object} args {cityPlanId, standbyMissionId, missionId}
 */
const coverMissionWithStandby = ({
  cityPlanId,
  standbyMissionId,
  missionId,
}) => {
  const query = `mutation ($input: CoverMissionWithStandbyInput!) {
    coverMissionWithStandby(input: $input) {
      standby { ${CLUSTER_MISSION_FIELDS} }
      noShow { ${CLUSTER_MISSION_FIELDS} }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, standbyMissionId, missionId } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * Sends an unused standby home on half pay, credited one hour.
 * @param {object} args {cityPlanId, standbyMissionId}
 */
const releaseStandby = ({ cityPlanId, standbyMissionId }) => {
  const query = `mutation ($input: ReleaseStandbyInput!) {
    releaseStandby(input: $input) { ${CLUSTER_MISSION_FIELDS} }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, standbyMissionId } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

const fetchCityPlanMissionStats = ({ cityPlanId, filter }) => {
  const query = `query ($cityPlanId: ID!, ${CITY_PLAN_FILTER_VARS}) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges { node {
        id
        missionStats(filter: $filter) {
          total
          readyCount
          withUserCount
          withoutUserCount
          withBoundaryCount
          byStatus { status count }
          weeks {
            key isoYear isoWeek startDate endDate
            total readyCount withUserCount withoutUserCount withBoundaryCount
            byStatus { status count }
            isEmptyPlaceholder
          }
        }
      } }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityPlanId, filter },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/**
 * The polygons overlapping a viewport (or all of them when bbox is null).
 * @param {object} args {cityPlanId, filter?, bbox?, first?, skip?}
 */
const fetchCityPlanMissionBoundaries = ({
  cityPlanId,
  filter,
  bbox,
  first,
  skip,
}) => {
  const query = `query ($cityPlanId: ID!, ${CITY_PLAN_FILTER_VARS}, $bbox: BBoxInput, $first: Int, $skip: Int) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges { node {
        id
        missionBoundaries(filter: $filter, bbox: $bbox, first: $first, skip: $skip) {
          totalCount
          pageInfo { hasNextPage endCursor }
          nodes {
            id label status isReadyToPublish userId isoWeekKey boundaryColour
            boundary { latitude longitude }
            meetingPoint householdsAmount areaKm2 density
          }
        }
      } }
    }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { cityPlanId, filter, bbox, first, skip },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/** One mission's polygon, for the single-area editor. */
const fetchMissionBoundary = missionId => {
  const query = `query ($missionId: ID!) {
    mission(id: $missionId) { id label boundary { latitude longitude } boundaryColour }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { missionId },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/** Flag / unflag every DRAFT mission in a selection. */
const setMissionsReadyToPublish = ({ cityPlanId, selection, ready }) => {
  const query = `mutation ($input: SetMissionsReadyToPublishInput!) {
    setMissionsReadyToPublish(input: $input) { ${BULK_RESULT_FIELDS} }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, selection, ready } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/** One patch for every editable mission in a selection. */
const updateMissionsBulk = ({
  cityPlanId,
  selection,
  patch,
  labelIndexOrderBy,
}) => {
  const query = `mutation ($input: UpdateMissionsBulkInput!) {
    updateMissionsBulk(input: $input) { ${BULK_RESULT_FIELDS} }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, selection, patch, labelIndexOrderBy } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/** Publish a selection; `job` is set when it runs in the background. */
const publishCityPlanMissions = ({ cityPlanId, selection }) => {
  const query = `mutation ($input: PublishCityPlanMissionsInput!) {
    publishCityPlanMissions(input: $input) { ${BULK_RESULT_FIELDS} }
  }`
  return callAuthorizedGraphQLEndpoint(
    query,
    { input: { cityPlanId, selection } },
    CITY_PLAN_CALL_OPTIONS,
  )
}

/** Progress of a background bulk job. */
const fetchMissionBulkJob = id => {
  const query = `query ($id: ID!) {
    missionBulkJob(id: $id) {
      id type status total processed failed error
      result { matchedCount updatedCount skipped { missionId reason } }
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, { id }, CITY_PLAN_CALL_OPTIONS)
}

export {
  getCityPowerBankHolds,
  fetchCityPlanMissionsPage,
  fetchCityPlanMissionStats,
  fetchMissionClusters,
  setStandbySlotsForPlan,
  bookShiftForUser,
  fetchCityAmbassadors,
  fetchCitySpyReportOptions,
  updateCitySpyReportOptions,
  fetchCampaignStandbySettings,
  updateCampaignStandbySettings,
  duplicateCityPlanDay,
  fetchMissionSwapCandidates,
  swapMissionAmbassador,
  sendStandbyHome,
  fetchMissionCluster,
  setStandbySlots,
  coverMissionWithStandby,
  releaseStandby,
  fetchCityPlanMissionBoundaries,
  fetchMissionBoundary,
  setMissionsReadyToPublish,
  updateMissionsBulk,
  publishCityPlanMissions,
  fetchMissionBulkJob,
  processInventoryMovements,
  getInventory,
  addInventoryItem,
  getInventoryItem,
  addFlyerCodes,
  getCampaignResults,
  getMissionsByCity,
  getMasterScheduleDetails,
  updateUserData,
  updateCity,
  deleteUser,
  getAmbassador,
  getAmbassadorsList,
  getCommonCityData,
  getCityUsers,
  createCity,
  getCountries,
  getCountriesAuthorized,
  getCountry,
  getDMPrintingCosts,
  getADMPrintingCosts,
  getStates,
  getPrinters,
  deletePrinter,
  createPrinter,
  updatePrinter,
  getPickupInventoryData,
  getPickupMissionData,
  getPitchQuestions,
  setPitchQuestions,
  unbookMission,
  deleteFlyerCodeById,
  updateCityPlanGQL,
  notifySuccessfullySpiedMissions,
  getMissionViewDataById,
  createMissions,
  updateMission,
  updateCampaign,
  getCampaignById,
  getCampaigns,
  getCities,
  createCityPlan,
  createCityPlans,
  getCityPlans,
  getClients,
  getMissionActivitiesLegacy,
  getMissionActivities,
  creatCitySpySettings,
  updateCitySpySettings,
  fetchCityPlanMissionFormChunks,
  getCampaignByChunks,
  getPitchUrl,
  getQuiz,
  setQuiz,
  duplicateQuiz,
  getCityCoords,
  importMissionsMasterSchedule,
  revertMissionDisqualification,
  getStatesTaxesForUS,
  updateBaCitySettingsGQL,
  updateMissionSimplified,
  updateQuiz,
  updateMissionNormalized,
  migrateUser,
  getCityCurrency,
  getPowerBankHoldSetting,
  setPowerBankHoldSetting,
  addPowerBankUnits,
  assignPowerBank,
  returnPowerBank,
  writeOffPowerBank,
  getAmbassadorPowerBanks,
}
